import { CheckIcon, MinusIcon } from 'lucide-react'
import { m } from 'motion/react'
import {
  Avatar as AvatarPrimitive,
  Checkbox as CheckboxPrimitive,
  Popover as PopoverPrimitive,
  Separator as SeparatorPrimitive,
  Switch as SwitchPrimitive,
  Tabs as TabsPrimitive,
  Tooltip as TooltipPrimitive,
} from 'radix-ui'
import type * as React from 'react'
import { createContext, useContext, useId, useState } from 'react'
import { SPRING } from '@/components/motion/tokens'
import { cn, initials } from '@/lib/utils'
import { menuSurface } from './dropdown-menu'

/* ---------- Tooltip ---------- */

export const TooltipProvider = TooltipPrimitive.Provider

interface TooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  shortcut?: string
  disabled?: boolean
}

export function Tooltip({ content, children, side = 'top', shortcut, disabled }: TooltipProps) {
  if (disabled) return <>{children}</>
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={8}
          className={cn(
            'z-50 flex items-center gap-2 rounded-md bg-foreground px-2 py-1 font-medium text-background text-xs shadow-elevation-2',
            'origin-(--radix-tooltip-content-transform-origin) duration-150 ease-smooth data-[state=closed]:duration-100',
            'fade-in-0 zoom-in-[0.97] data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-[0.97] animate-in data-[state=closed]:animate-out',
          )}
        >
          {content}
          {shortcut && <span className="text-background/60">{shortcut}</span>}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

/* ---------- Popover ---------- */

export const Popover = PopoverPrimitive.Root
export const PopoverTrigger = PopoverPrimitive.Trigger
export const PopoverAnchor = PopoverPrimitive.Anchor

export function PopoverContent({
  className,
  align = 'start',
  sideOffset = 6,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        className={cn(menuSurface, 'p-0 outline-none', className)}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
}

/* ---------- Switch ---------- */

export function Switch({ className, ...props }: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      className={cn(
        'group peer inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-transparent transition-colors duration-200 ease-smooth',
        'bg-input data-[state=checked]:bg-primary disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="pointer-events-none block size-4 translate-x-0.5 rounded-full bg-white shadow-elevation-1 ring-0 transition-[translate,scale] duration-200 ease-smooth group-active:scale-90 data-[state=checked]:translate-x-[17px]" />
    </SwitchPrimitive.Root>
  )
}

/* ---------- Checkbox ---------- */

export function Checkbox({ className, ...props }: React.ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root
      className={cn(
        'peer flex size-4 shrink-0 items-center justify-center rounded-[5px] border border-input bg-card shadow-elevation-1 transition duration-150 ease-smooth active:scale-90',
        'data-[state=checked]:border-primary data-[state=indeterminate]:border-primary data-[state=checked]:bg-primary data-[state=indeterminate]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:text-primary-foreground',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="fade-in-0 zoom-in-50 flex animate-in duration-150 ease-smooth">
        {props.checked === 'indeterminate' ? (
          <MinusIcon className="size-3" strokeWidth={3} />
        ) : (
          <CheckIcon className="size-3" strokeWidth={3} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

/* ---------- Separator ---------- */

export function Separator({
  className,
  orientation = 'horizontal',
  ...props
}: React.ComponentProps<typeof SeparatorPrimitive.Root>) {
  return (
    <SeparatorPrimitive.Root
      orientation={orientation}
      className={cn(
        'shrink-0 bg-border',
        orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px',
        className,
      )}
      {...props}
    />
  )
}

/* ---------- Avatar ---------- */

const avatarHues = [278, 178, 25, 340, 235, 150, 60]

export function Avatar({ name, src, className }: { name: string; src?: string | null; className?: string }) {
  const hue = avatarHues[[...name].reduce((acc, ch) => acc + ch.charCodeAt(0), 0) % avatarHues.length]
  return (
    <AvatarPrimitive.Root
      className={cn('relative flex size-8 shrink-0 overflow-hidden rounded-full text-xs', className)}
    >
      {src && <AvatarPrimitive.Image src={src} alt={name} className="aspect-square size-full object-cover" />}
      <AvatarPrimitive.Fallback
        className="flex size-full items-center justify-center font-semibold text-white"
        style={{
          background: `linear-gradient(135deg, oklch(0.62 0.17 ${hue}), oklch(0.5 0.19 ${(hue ?? 0) + 30}))`,
        }}
      >
        {initials(name)}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  )
}

/* ---------- Tabs ---------- */

/** Pestaña activa e identificador único del grupo, para que la pastilla se deslice entre pestañas (layoutId). */
const TabsContext = createContext<{ value?: string; id: string } | null>(null)

export function Tabs({
  value,
  defaultValue,
  onValueChange,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  const [inner, setInner] = useState(defaultValue)
  const id = useId()
  return (
    <TabsContext value={{ value: value ?? inner, id }}>
      <TabsPrimitive.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={(v) => {
          setInner(v)
          onValueChange?.(v)
        }}
        {...props}
      />
    </TabsContext>
  )
}

export function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      className={cn(
        'inline-flex h-9 max-w-full items-center gap-0.5 overflow-x-auto rounded-lg bg-muted p-0.5 text-muted-foreground [scrollbar-width:none]',
        className,
      )}
      {...props}
    />
  )
}

export function TabsTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const tabs = useContext(TabsContext)
  const active = !!tabs && tabs.value === props.value
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'relative inline-flex h-full shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 font-medium text-[13px] transition-colors duration-150',
        'hover:text-foreground data-[state=active]:text-foreground [&_svg]:size-4',
        // Sin contexto (Root de Radix directo) el fondo activo lo pone el CSS, sin deslizarse.
        !tabs &&
          'data-[state=active]:bg-card data-[state=active]:shadow-elevation-1 dark:data-[state=active]:bg-white/10',
        className,
      )}
      {...props}
    >
      {active && (
        <m.span
          layoutId={`tab-pill-${tabs.id}`}
          transition={SPRING.snappy}
          className="absolute inset-0 rounded-md bg-card shadow-elevation-1 dark:bg-white/10"
        />
      )}
      <span className="relative inline-flex items-center gap-[inherit]">{children}</span>
    </TabsPrimitive.Trigger>
  )
}

export const TabsContent = TabsPrimitive.Content

/* ---------- Kbd ---------- */

export function Kbd({ className, ...props }: React.ComponentProps<'kbd'>) {
  return (
    <kbd
      className={cn(
        'pointer-events-none inline-flex h-5 min-w-5 select-none items-center justify-center gap-0.5 rounded border border-border',
        'bg-muted px-1 font-medium font-sans text-[10.5px] text-muted-foreground',
        className,
      )}
      {...props}
    />
  )
}
